/**
 * ApiConnectorsSection — the "API connectors" section of the agent editor's
 * Connectors tab: a REST API described by an OpenAPI document, plugged into
 * THIS agent.
 *
 * The editor reads a document (pasted text, or a URL),
 * picks the operations the agent may call, says what the model sees of each
 * response, sets auth and writes the agent a guide. Like the MCP section,
 * a connector is its own record: nothing rides the agent form and a NEW
 * agent must be saved first.
 *
 * The dialog is two columns: the API on one side (document, address, auth),
 * what the agent gets on the other (operations, guide).
 *
 * The same form edits the DEFAULTS of an entry on an organization's
 * connector list (`ApiConnectorForm` in `mode="defaults"`, backed by the
 * list's endpoints) — one editor, two homes, so the two cannot drift apart.
 * On an agent the rows live in the tab's one Services list, whose Add opens
 * the organization's list ("Custom API" opens the form below). Every
 * connector carries its status chip; a shared one shows what following the
 * list means and offers "Customize for this agent" before any editor opens.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Switch, Tooltip } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { showNotification } from '@/utils/showNotification';
import {
  apiConnectorService,
  type ApiAuthType,
  type ApiConnector,
  type ApiOperationSettings,
  type ReadSpecResult,
  type TryResult,
} from '../services/apiConnectorService';
import {
  ConnectorDialog, DialogColumn, DialogColumns, Field, PrimaryAction, ReadOnlyValue, SaveCancelled,
  dialogFrame, isSaveCancelled, phraseConnectorError, switchesOnWrites, useRiskAcknowledgement, type FormFrame,
} from './ConnectorDialog';
import {
  AdvancedSection, ChevronGlyph, DEFAULT_MAX_RESULT_CHARS, InUseMark, connectorAdvancedInUse, operationAdvancedInUse,
} from './ConnectorAdvanced';
import { SignInChoice, SignInSummary } from './SignInChoice';
import { apiAuthOf, apiSignIn, personSignIn, signInComplete, type SignInValue } from '../utils/signIn';
import { GuideDraft, type DraftScope } from './GuideDraft';
import type { AgentConnectorLibrary } from '@modules/connector_library/hooks/useAgentConnectorLibrary';
import { useConfirmStep } from '@modules/connector_library/hooks/useConfirmStep';
import { LibraryConnectorStatus } from '@modules/connector_library/components/ConnectorStatus';
import { CredentialFields, ServiceIcon, credentialComplete } from '@modules/connector_library/components/LibraryBits';
import type { LibraryCredential } from '@modules/connector_library/services/connectorLibraryService';
import {
  LIBRARY_DESCRIPTION_MAX, withLibraryStatus,
} from '@modules/connector_library/services/connectorLibraryService';

interface Props {
  chatbotId?: string;
  disabled?: boolean;
  /** The organization's connector list, when this agent may use it. */
  library?: AgentConnectorLibrary;
  /** The custom form is open (the list's "Custom API"). */
  customAddOpen?: boolean;
  onCustomAddClose?: () => void;
  /** How many connectors it lists, for the tab's empty state. */
  onCount?: (count: number) => void;
}

/** Mirrors the backend limits (api_connector_service.py, connector_library_service.py). */
const MAX_NAME_LENGTH = 80;
const MAX_INSTRUCTIONS_CHARS = 6000;

/** What a connector that follows the organization's list uses, read-only —
    the actions it may call, its cache and result size, and its guide — so
    an editor sees what it inherits before deciding to customize. */
function InheritedSettings({ connector: c }: { connector: ApiConnector }) {
  const { t } = useLanguage();
  const on = c.operations.filter(op => op.settings?.enabled);
  return (
    <dl className="cxl-inherited" data-testid={`api-inherited-${c.slug}`}>
      <dt>{t('connectorLibrary.inherited.actions')}</dt>
      <dd>{on.length ? on.map(op => <code key={op.operation_id} dir="ltr">{op.operation_id}</code>) : '—'}</dd>
      <dt>{t('bots.connectors.api.cacheLabel')}</dt>
      <dd dir="ltr">{c.cache_ttl_seconds ? t('connectorLibrary.inherited.seconds', { count: c.cache_ttl_seconds }) : t('connectorLibrary.inherited.off')}</dd>
      <dt>{t('bots.connectors.api.instructionsLabel')}</dt>
      <dd>
        {c.instructions
          ? <div className="cxl-inherited__guide" dir="auto" data-testid={`api-inherited-guide-${c.slug}`}>{c.instructions}</div>
          : <span className="cxl-inherited__none">{t('connectorLibrary.inherited.noGuide')}</span>}
      </dd>
    </dl>
  );
}

type SettingsMap = Record<string, ApiOperationSettings>;

/** Text-field helpers: one path per line; a JSON object for pinned/defaults. */
const linesToList = (text: string) => text.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
const listToLines = (list?: string[]) => (list || []).join('\n');
const objToText = (obj?: Record<string, unknown>) => (obj && Object.keys(obj).length ? JSON.stringify(obj) : '');
function textToObj(text: string): Record<string, unknown> | null {
  const t = text.trim();
  if (!t) return {};
  try {
    const parsed = JSON.parse(t);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export default function ApiConnectorsSection({
  chatbotId, disabled, library, customAddOpen, onCustomAddClose, onCount,
}: Props) {
  const { t } = useLanguage();
  const [connectors, setConnectors] = useState<ApiConnector[]>([]);
  // A count only from a list that arrived: a failed read is not "none".
  const [listed, setListed] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ connector: ApiConnector; customizing?: boolean } | null>(null);
  // A connector that follows the organization's list, opened: what that
  // means, and "Customize for this agent" — never straight into the editor.
  const [syncedId, setSyncedId] = useState<string | null>(null);
  const [tryFor, setTryFor] = useState<ApiConnector | null>(null);
  const [pendingRemove, setPendingRemove] = useState<ApiConnector | null>(null);
  const { confirm, modal: confirmModal } = useConfirmStep();

  const load = useCallback(async () => {
    if (!chatbotId) return;
    try {
      setConnectors(await apiConnectorService.list(chatbotId));
      setListed(true);
    } catch {
      setListed(false);
      showNotification(t('bots.connectors.api.loadError'), 'error');
    }
  }, [chatbotId, t]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => { if (listed) onCount?.(connectors.length); }, [listed, connectors.length, onCount]);

  const closeDialog = () => setDialog(null);
  const closeAdd = () => onCustomAddClose?.();

  const replace = (next: ApiConnector) =>
    setConnectors(prev => prev.map(c => (c.connector_id === next.connector_id ? withLibraryStatus(c, next) : c)));

  const phrase = (err: unknown, fallbackKey: string): string => phraseConnectorError(t, err, fallbackKey);

  const withBusy = async (c: ApiConnector, fn: () => Promise<void>) => {
    setBusyId(c.connector_id);
    try {
      await fn();
    } catch (err) {
      showNotification(phrase(err, 'bots.connectors.api.updateError'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const toggleEnabled = (c: ApiConnector) => withBusy(c, async () => {
    replace(await apiConnectorService.update(chatbotId!, c.connector_id, { enabled: !c.enabled }));
  });

  const confirmRemove = () => {
    const c = pendingRemove;
    if (!c) return;
    setPendingRemove(null);
    void withBusy(c, async () => {
      await apiConnectorService.remove(chatbotId!, c.connector_id);
      setConnectors(prev => prev.filter(x => x.connector_id !== c.connector_id));
      if (c.library_entry_id) void library?.reload();
    });
  };

  const agentBackend = useMemo(() => (chatbotId ? agentApiBackend(chatbotId) : null), [chatbotId]);

  // Editing a shared connector on this agent makes it customized: the person
  // chose "Customize for this agent" first; a save that reached the editor any
  // other way still asks, and backing out sends nothing.
  const editing = dialog?.connector ?? null;
  const customizing = !!dialog?.customizing;
  const editBackend = useMemo<ApiConnectorFormBackend<ApiConnector> | null>(() => {
    if (!agentBackend || editing?.library?.chip !== 'shared' || customizing) return agentBackend;
    return {
      ...agentBackend,
      update: async (id, payload) => {
        // A new key or an unchanged save is not a customization (the server
        // agrees: it compares everything but the credential), so no warning.
        if (!changesDefaults(editing, payload)) return agentBackend.update(id, payload);
        const ok = await confirm({
          title: t('connectorLibrary.customize.title', { name: editing.name }),
          message: t('connectorLibrary.customize.body'),
          confirmText: t('common.save'),
          cancelText: t('common.cancel'),
          variant: 'warning',
        });
        if (!ok) throw new SaveCancelled();
        return agentBackend.update(id, payload);
      },
    };
  }, [agentBackend, editing, customizing, confirm, t]);

  const openEdit = (c: ApiConnector) => {
    if (c.library?.chip === 'shared') setSyncedId(syncedId === c.connector_id ? null : c.connector_id);
    else setDialog({ connector: c });
  };

  return (
    <div data-testid="api-connectors-section">
      {connectors.length > 0 && (
        <div className="cxs__list">
          {connectors.map(c => {
            const busy = busyId === c.connector_id;
            const on = c.operations.filter(op => op.settings.enabled).length;
            const cites = c.operations.some(op => op.settings.enabled && (op.settings.verifies || []).length > 0);
            return (
              <LibraryConnectorStatus key={c.connector_id} chatbotId={chatbotId!} kind="api" connector={c}
                                      disabled={disabled || busy} onChanged={replace}>
                {({ chips, panel }) => (
                  <div className={`cxc cxc--column ${c.enabled ? 'is-on' : ''} ${busy ? 'is-busy' : ''}`}
                       data-testid={`api-connector-${c.slug}`}>
                    <div className="cxc__row">
                      <ServiceIcon name={c.name} slug={c.slug} url={c.base_url} className="cxc__icon"
                                   fallback={<span className="cxc__icon" aria-hidden="true"><ApiGlyph /></span>} />
                      <span className="cxc__body">
                        <span className="cxc__name" dir="auto">{c.name}</span>
                        <span className="cxc__meta">
                          {/* The document's own title and version are detail: on hover. */}
                          <span className="cxc__url" dir="ltr"
                                title={c.spec_title ? `${c.spec_title}${c.spec_version ? ` ${c.spec_version}` : ''}` : undefined}>
                            {c.base_url}
                          </span>
                        </span>
                        <span className="cxc__pills">
                          <span className="cx-pill cx-pill--ok">
                            {t('bots.connectors.api.operationsOn', { on, total: c.operations.length })}
                          </span>
                          {c.has_auth && <span className="cx-pill">{t('bots.connectors.mcp.hasAuth')}</span>}
                          {cites && (
                            <Tooltip title={t('bots.connectors.api.citationsHint')}>
                              <span className="cx-pill">{t('bots.connectors.api.citations')}</span>
                            </Tooltip>
                          )}
                          {chips}
                        </span>
                      </span>
                      <span className="cxc__end">
                        <span className="cxc__actions">
                          <button type="button" className="cx-btn cx-btn--sm" disabled={disabled || busy}
                                  onClick={() => setTryFor(c)} data-testid={`api-try-${c.slug}`}>
                            {t('bots.connectors.api.tryIt')}
                          </button>
                          <button type="button" className="cx-btn cx-btn--sm" disabled={disabled || busy}
                                  onClick={() => openEdit(c)} aria-expanded={c.library?.chip === 'shared' ? syncedId === c.connector_id : undefined}
                                  data-testid={`api-edit-${c.slug}`}>
                            {c.library?.chip === 'shared' ? t('connectorLibrary.synced.open') : t('common.edit')}
                          </button>
                          <button type="button" className="cx-btn cx-btn--sm cx-btn--danger" disabled={disabled || busy}
                                  onClick={() => setPendingRemove(c)} data-testid={`api-remove-${c.slug}`}>
                            {t('common.remove')}
                          </button>
                        </span>
                        <Switch checked={c.enabled} disabled={disabled || busy} onChange={() => toggleEnabled(c)}
                                data-testid={`api-enabled-${c.slug}`} />
                      </span>
                    </div>
                    {syncedId === c.connector_id && c.library?.chip === 'shared' && (
                      <div className="cxl-synced" data-testid={`api-synced-${c.slug}`}>
                        <p className="cxl-synced__text">
                          {c.library.organization
                            ? <>{t('connectorLibrary.synced.bodyOrg')} <span dir="auto">{c.library.organization}</span>. {t('connectorLibrary.synced.updates')}</>
                            : <>{t('connectorLibrary.synced.body')} {t('connectorLibrary.synced.updates')}</>}
                        </p>
                        <InheritedSettings connector={c} />
                        <div className="cxl-synced__actions">
                          <button type="button" className="cx-btn cx-btn--sm" onClick={() => setSyncedId(null)}>
                            {t('common.cancel')}
                          </button>
                          <button type="button" className="cx-btn cx-btn--sm" disabled={disabled || busy}
                                  onClick={() => { setSyncedId(null); setDialog({ connector: c, customizing: true }); }}
                                  data-testid={`api-customize-${c.slug}`}>
                            {t('connectorLibrary.synced.customize')}
                          </button>
                          <span className="cxl-synced__hint">{t('connectorLibrary.synced.customizeHint')}</span>
                        </div>
                      </div>
                    )}
                    {panel}
                  </div>
                )}
              </LibraryConnectorStatus>
            );
          })}
        </div>
      )}

      {chatbotId && editBackend && editing && (
        <ApiConnectorForm
          backend={editBackend}
          draftScope={{ chatbotId }}
          existing={connectorAsExisting(editing)}
          onClose={closeDialog}
          onSaved={(c) => {
            replace(c);
            setDialog(null);
            // The list status (shared key, needs a key) may have moved with it.
            if (c.library_entry_id) void load();
          }}
          phrase={phrase}
        />
      )}

      {chatbotId && agentBackend && customAddOpen && (
        <ApiConnectorForm
          backend={agentBackend}
          draftScope={{ chatbotId }}
          existing={null}
          onClose={closeAdd}
          onSaved={(c) => { setConnectors(prev => [...prev, c]); closeAdd(); }}
          phrase={phrase}
        />
      )}

      {confirmModal}

      {chatbotId && tryFor && (
        <TryDialog chatbotId={chatbotId} connector={tryFor} onClose={() => setTryFor(null)} phrase={phrase} />
      )}

      <ConfirmModal
        isOpen={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={confirmRemove}
        title={t('bots.connectors.mcp.removeTitle', { name: pendingRemove?.name ?? '' })}
        message={t('bots.connectors.api.removeBody')}
        confirmText={t('common.remove')}
        cancelText={t('common.cancel')}
        variant="danger"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create / edit — on an agent, or a list entry's settings
// ---------------------------------------------------------------------------

/** Where the form reads documents from and saves to. */
export interface ApiConnectorFormBackend<T> {
  readSpec: (input: { spec_text?: string; spec_url?: string }) => Promise<ReadSpecResult>;
  create: (payload: Record<string, unknown>) => Promise<T>;
  update: (id: string, payload: Record<string, unknown>) => Promise<T>;
  refresh: (id: string, specText: string) => Promise<T>;
}

/** What the form needs of a saved connector (an agent's, or a list entry). */
export interface ApiConnectorFormExisting {
  id: string;
  name: string;
  base_url: string;
  spec_url: string | null;
  spec_title: string | null;
  spec_version: string | null;
  operations: ApiConnector['operations'];
  instructions: string;
  auth_type: ApiAuthType;
  /** A secret is stored (the agent's own, or the organization's shared one). */
  has_auth: boolean;
  cache_ttl_seconds: number;
  max_result_chars: number;
  /** Defaults mode only. */
  description?: string;
  auth_meta?: { in?: 'header' | 'query'; name?: string };
  /** An agent's connector attached to a list entry each person signs in to: that sign-in, as the list sets it. */
  person_signin?: SignInValue | null;
}

/** The agent's endpoints, as a form backend. */
function agentApiBackend(chatbotId: string): ApiConnectorFormBackend<ApiConnector> {
  return {
    readSpec: (input) => apiConnectorService.readSpec(chatbotId, input),
    create: (payload) => apiConnectorService.create(chatbotId, payload as never),
    update: (id, payload) => apiConnectorService.update(chatbotId, id, payload as never),
    refresh: (id, specText) => apiConnectorService.refresh(chatbotId, id, specText),
  };
}

function connectorAsExisting(c: ApiConnector): ApiConnectorFormExisting {
  const person = personSignIn(c.library);
  return { ...c, id: c.connector_id,
           person_signin: person ? apiSignIn(person, c.library?.auth_type, c.library?.auth_meta) : null };
}

interface FormProps<T> {
  backend: ApiConnectorFormBackend<T>;
  /** Where the connector is edited (an agent, or a library entry): "Write a draft" is allowed there. */
  draftScope: DraftScope;
  existing: ApiConnectorFormExisting | null;
  onClose: () => void;
  onSaved: (saved: T) => void;
  phrase: (err: unknown, fallbackKey: string) => string;
  /** 'defaults' edits an entry of an organization's connector list: it adds
      the description people see when adding it; how it signs in is the
      entry's Sign-in tab's (item 80). */
  mode?: 'agent' | 'defaults';
  /** Defaults mode: what saving does, said once above the form. */
  banner?: ReactNode;
  /** Defaults mode: "Last changed by Dana, 2 days ago". */
  footerMeta?: ReactNode;
  /** Defaults mode: the one line saying how it signs in, with the way to the
      Sign-in tab, where the whole question is answered (item 80). */
  signInSummary?: ReactNode;
  /** Defaults mode, an entry inherited from an organization above: only the
      name, description, operations, guide and cache are this organization's
      to change — the address, the document and the credential are shown,
      "Set by <owner>". */
  layer?: { ownerName: string };
  /** Where the form is drawn: its own dialog (default), or a host's frame. */
  frame?: FormFrame;
}

/** Whether an edit changes anything but the on/off switch and the key —
    what makes a connector that follows the organization's list customized. */
function changesDefaults(c: ApiConnector, payload: Record<string, unknown>): boolean {
  const settings = Object.fromEntries((c.operations || []).map(op => [op.operation_id, op.settings]));
  const current: Record<string, unknown> = {
    name: c.name, base_url: c.base_url, instructions: c.instructions || '',
    cache_ttl_seconds: c.cache_ttl_seconds, max_result_chars: c.max_result_chars, operation_settings: settings,
  };
  return Object.entries(payload).some(([key, value]) => {
    if (key === 'auth' || key === 'auth_type' || key === 'enabled' || !(key in current)) return false;
    return JSON.stringify(value ?? '') !== JSON.stringify(current[key] ?? '');
  });
}

export function ApiConnectorForm<T>({
  backend, existing, onClose, onSaved, phrase, mode = 'agent', banner, footerMeta, signInSummary, layer, frame = dialogFrame, draftScope,
}: FormProps<T>) {
  const { t } = useLanguage();
  const defaultsMode = mode === 'defaults';
  const layerMode = defaultsMode && !!layer && !!existing;
  const [name, setName] = useState(existing?.name ?? '');
  const [baseUrl, setBaseUrl] = useState(existing?.base_url ?? '');
  const [specText, setSpecText] = useState('');
  const [specUrl, setSpecUrl] = useState(existing?.spec_url ?? '');
  const [read, setRead] = useState<ReadSpecResult | null>(
    existing ? { title: existing.spec_title, version: existing.spec_version, base_url: existing.base_url,
                 operations: existing.operations } : null,
  );
  /** The document text/URL the catalogue on screen was read from. Editing the
      document after reading it invalidates the catalogue until it is re-read —
      the server re-parses whatever is sent, and settings for operations that
      no longer exist would silently vanish. */
  const [readSource, setReadSource] = useState<string>('');
  const [settings, setSettings] = useState<SettingsMap>(
    () => Object.fromEntries((existing?.operations ?? []).map(op => [op.operation_id, { ...op.settings }])),
  );
  const [instructions, setInstructions] = useState(existing?.instructions ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  // The agent's own key (a list entry's sign-in is its Sign-in tab's, item 80).
  const [signIn, setSignInValue] = useState<SignInValue>(() => apiSignIn(
    existing && existing.auth_type !== 'none' ? 'per_agent' : 'none', existing?.auth_type, existing?.auth_meta));
  const [authTouched, setAuthTouched] = useState(false);
  const [cacheTtl, setCacheTtl] = useState<number>(existing?.cache_ttl_seconds ?? 3600);
  // Under Advanced: kept as stored; left empty, the server's default applies.
  const [maxResultChars, setMaxResultChars] = useState<number | undefined>(existing?.max_result_chars ?? undefined);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  // A saved connector already has its document: it is shown, and only
  // "Replace document" opens the paste box (an empty box read as if none).
  const [replacing, setReplacing] = useState(!existing);

  const readDocument = async () => {
    setError(null);
    setReading(true);
    try {
      const result = await backend.readSpec(specText.trim() ? { spec_text: specText } : { spec_url: specUrl.trim() });
      setRead(result);
      setReadSource(specText.trim() ? specText : specUrl.trim());
      if (!baseUrl && result.base_url) setBaseUrl(result.base_url);
      if (!name && result.title) setName(result.title.slice(0, MAX_NAME_LENGTH));
      setSettings(prev => Object.fromEntries(result.operations.map(op => [op.operation_id, prev[op.operation_id] || { enabled: false }])));
    } catch (err) {
      setError(phrase(err, 'bots.connectors.api.errors.generic'));
    } finally {
      setReading(false);
    }
  };

  const setOp = (id: string, patch: Partial<ApiOperationSettings>) =>
    setSettings(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const setSignIn = (v: SignInValue) => { setSignInValue(v); setAuthTouched(true); };
  // What an agent's own connector has always sent: no sign-in is `auth: null`.
  const agentAuth = { auth_type: apiAuthOf(signIn, true).auth_type, auth: signIn.mode === 'none' ? null : apiAuthOf(signIn, true).auth };
  const storedSecretKept = !!existing?.has_auth && !authTouched;
  const baseUrlChanged = !!existing && baseUrl.trim() !== existing.base_url;
  // A shared key goes only to the address it was entered for: a new address takes it again here, or drops it.
  const [addressKey, setAddressKey] = useState<LibraryCredential>({});
  const asksAddressKey = defaultsMode && !layerMode && baseUrlChanged && !!existing?.has_auth && existing.auth_type !== 'none';
  const addressKeyPayload = asksAddressKey && existing && credentialComplete({ kind: 'api', auth_type: existing.auth_type }, addressKey)
    ? { ...apiAuthOf({ ...apiSignIn('shared', existing.auth_type, existing.auth_meta), value: addressKey.value ?? '',
                        username: addressKey.username ?? '', password: addressKey.password ?? '' }, true),
        share_credential: true }
    : {};
  // Asked only once the sign-in is changed: a connector following the list uses the organization's key by
  // reference and holds no copy of its own, and must stay savable as it is.
  const signInMissing = !defaultsMode && authTouched && !signInComplete('agent', signIn, false);

  const enabledCount = Object.values(settings).filter(s => s.enabled).length;
  // Actions whose advanced settings differ from the defaults — said even while closed.
  const opsInUse = (read?.operations ?? []).filter(op => operationAdvancedInUse(settings[op.operation_id])).length;
  const documentChanged = specText.trim()
    ? specText !== readSource
    : (!existing && specUrl.trim() !== readSource);
  // What the form held when it opened: an existing connector's Save stays
  // off until something actually changed (the library entry's footer says so).
  const snapshot = JSON.stringify([name, baseUrl, specText, specUrl, settings, instructions, description, signIn,
                                   addressKey, cacheTtl, maxResultChars]);
  const opened = useRef(snapshot);
  const dirty = snapshot !== opened.current;
  // The risk acknowledgement beside Save (item 69): an API added here by
  // hand, or operations that change data switched on by this save.
  const switchesOn = useMemo(() => {
    if (!existing) return false;
    const was = new Set(existing.operations.filter(o => o.settings?.enabled).map(o => o.operation_id));
    return switchesOnWrites((read?.operations ?? []).map(op => ({
      writes: op.writes, was: was.has(op.operation_id), now: !!settings[op.operation_id]?.enabled })));
  }, [existing, read, settings]);
  const ack = useRiskAcknowledgement({ customAdd: !existing && !defaultsMode, switchesOn,
                                       service: name.trim() || existing?.name });
  const canSave = name.trim().length > 0 && baseUrl.trim().length > 0 && !!read && enabledCount > 0
    && !documentChanged && !signInMissing && !saving && (!existing || dirty) && ack.ok;

  // Emptied after it had a value = back to the default the hint names (an
  // absent key would keep the old value); never set = not sent at all.
  const maxChars = maxResultChars
    ?? (existing?.max_result_chars != null ? DEFAULT_MAX_RESULT_CHARS : undefined);

  const submit = async () => {
    setError(null);
    setSaving(true);
    try {
      const common = {
        name: name.trim(), operation_settings: settings,
        instructions: instructions.trim(), cache_ttl_seconds: cacheTtl,
        ...(maxChars ? { max_result_chars: maxChars } : {}),
        // Only a changed base URL is re-validated (it resolves DNS server-side).
        ...(existing && baseUrl.trim() === existing.base_url ? {} : { base_url: baseUrl.trim() }),
        ...(defaultsMode ? { description: description.trim() } : {}),
      };
      let saved: T;
      if (existing && layerMode) {
        // An inherited entry: only what this organization may change.
        saved = await backend.update(existing.id, {
          name: name.trim(), description: description.trim(), operation_settings: settings,
          instructions: instructions.trim(), cache_ttl_seconds: cacheTtl,
          ...(maxChars ? { max_result_chars: maxChars } : {}),
          ...ack.payload,
        });
      } else if (existing) {
        saved = await backend.update(existing.id, {
          ...common,
          ...(authTouched ? agentAuth : {}),
          ...addressKeyPayload,
          ...ack.payload,
        });
        if (specText.trim()) saved = await backend.refresh(existing.id, specText);
      } else {
        saved = await backend.create({
          ...common,
          ...(specText.trim() ? { spec_text: specText } : { spec_url: specUrl.trim() }),
          ...agentAuth,
          ...ack.payload,
        });
        if (!defaultsMode) showNotification(t('bots.connectors.api.connected', { count: enabledCount }), 'success');
      }
      onSaved(saved);
    } catch (err) {
      ack.onError(err);
      if (!isSaveCancelled(err)) setError(phrase(err, 'bots.connectors.api.errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const formNote: ReactNode = error
    ? error
    : (read && documentChanged ? <span data-testid="api-spec-stale">{t('bots.connectors.api.specChanged')}</span> : footerMeta ?? null);
  const setBy = layer ? t('connectorLibrary.editor.setBy', { organization: layer.ownerName }) : undefined;
  const documentName = [existing?.spec_title, existing?.spec_version].filter(Boolean).join(' ');

  return frame({
    size: 'wide',
    icon: <ApiGlyph />,
    // Defaults mode is drawn by the library entry window, which titles it itself.
    title: defaultsMode ? '' : (existing ? t('bots.connectors.api.editTitle') : t('bots.connectors.api.addTitle')),
    subtitle: defaultsMode ? undefined : (existing ? t('bots.connectors.api.editHint') : t('bots.connectors.api.addHint')),
    onClose,
    note: formNote,
    noteTone: error ? 'error' : 'muted',
    testId: 'api-connector-dialog',
    dirty,
    acknowledge: ack.node,
    actions: (<>
      <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>{t('common.cancel')}</button>
      <PrimaryAction label={defaultsMode ? t('connectorLibrary.editor.save')
                       : (existing ? t('common.save') : t('bots.connectors.api.connect'))}
                     busyLabel={t('common.saving')}
                     busy={saving} disabled={!canSave} onClick={submit} testId="api-dialog-submit" />
    </>),
    children: (<>
      {error && <span hidden data-testid="api-dialog-error">{error}</span>}
      {banner}
      <DialogColumns>
        {/* ── The API ─────────────────────────────────────────────────── */}
        <DialogColumn title={t('bots.connectors.api.sourceTitle')}>
          <Field label={t('bots.connectors.mcp.nameLabel')} htmlFor="api-name">
            <input id="api-name" className="form-input" value={name} onChange={e => setName(e.target.value)}
                   maxLength={MAX_NAME_LENGTH} dir="auto" data-testid="api-name" />
          </Field>

          {defaultsMode && (
            <Field label={t('connectorLibrary.editor.descriptionLabel')} hint={t('connectorLibrary.editor.descriptionHint')}
                   htmlFor="api-description">
              <textarea id="api-description" className="form-input form-textarea" rows={2} value={description}
                        onChange={e => setDescription(e.target.value)} maxLength={LIBRARY_DESCRIPTION_MAX} dir="auto"
                        data-testid="library-description" />
            </Field>
          )}

          {layerMode ? (
            <>
              <Field label={t('bots.connectors.api.baseUrlLabel')} hint={setBy}>
                <ReadOnlyValue mono testId="api-base-url-readonly">{existing?.base_url}</ReadOnlyValue>
              </Field>
              <Field label={t('bots.connectors.api.specLabel')} hint={setBy}>
                <ReadOnlyValue testId="api-spec-readonly">
                  {existing?.spec_url
                    ? <code dir="ltr">{existing.spec_url}</code>
                    : <span dir="auto">{[existing?.spec_title, existing?.spec_version].filter(Boolean).join(' ') || '—'}</span>}
                </ReadOnlyValue>
              </Field>
              {signInSummary}
            </>
          ) : (<>
          <Field label={t('bots.connectors.api.baseUrlLabel')} htmlFor="api-base-url"
                 hint={asksAddressKey ? t('connectorLibrary.signin.addressDropsKey') : undefined}>
            <input id="api-base-url" className="form-input form-input--mono" value={baseUrl}
                   onChange={e => setBaseUrl(e.target.value)} dir="ltr" placeholder="https://api.example.com"
                   data-testid="api-base-url" />
          </Field>
          {asksAddressKey && existing && (
            <div data-testid="library-address-key">
              <CredentialFields entry={{ kind: 'api', auth_type: existing.auth_type, auth_meta: existing.auth_meta }}
                                value={addressKey} onChange={setAddressKey} />
            </div>
          )}

          {existing && !replacing ? (
            <Field label={t('bots.connectors.api.specLabel')}>
              <div className="cxd__source" data-testid="api-spec-summary">
                <ReadOnlyValue>
                  {existing.spec_url
                    ? <code dir="ltr">{existing.spec_url}</code>
                    : <span dir="auto">{documentName || t('bots.connectors.api.specPasted')}</span>}
                </ReadOnlyValue>
                <button type="button" className="cx-btn cx-btn--sm" onClick={() => setReplacing(true)}
                        data-testid="api-spec-replace">
                  {t('bots.connectors.api.replaceDocument')}
                </button>
              </div>
            </Field>
          ) : (
          <Field label={t('bots.connectors.api.specLabel')} hint={t('bots.connectors.api.specHint')} htmlFor="api-spec-text">
            <div className="cxd__stack">
              <textarea id="api-spec-text" className="form-input form-textarea form-input--mono" rows={8} value={specText}
                        onChange={e => setSpecText(e.target.value)} dir="ltr"
                        placeholder={'{ "openapi": "3.0.0", ... }'} data-testid="api-spec-text" />
              <div className="cxd__row">
                <input className="form-input form-input--mono" value={specUrl} onChange={e => setSpecUrl(e.target.value)}
                       dir="ltr" placeholder="https://example.com/openapi.json"
                       aria-label={t('bots.connectors.api.specLabel')} data-testid="api-spec-url" />
                <button type="button" className="cx-btn" onClick={readDocument}
                        disabled={reading || (!specText.trim() && !specUrl.trim())} data-testid="api-read-spec">
                  {reading ? t('common.loading') : t('bots.connectors.api.readSpec')}
                </button>
              </div>
            </div>
          </Field>
          )}

          {defaultsMode ? signInSummary : existing?.person_signin ? (
            // Each person signs in for themselves, as the organization's list sets it: nothing to choose here.
            <Field label={t('bots.connectors.api.authLabel')} hint={t('connectorLibrary.signin.personFromList')}>
              <p className="cxl-signin-line" data-testid="agent-signin-person">
                <span className="cxl-signin-line__value"><SignInSummary kind="api" value={existing.person_signin} /></span>
              </p>
              <p className="cxl-signin__note">{t(`connectorLibrary.signin.hint.${existing.person_signin.mode}`)}</p>
            </Field>
          ) : (
            <Field label={t('bots.connectors.api.authLabel')}
                   hint={existing?.has_auth && !authTouched ? t('bots.connectors.api.authKept') : t('bots.connectors.api.authHint')}>
              <SignInChoice kind="api" scope="agent" value={signIn} onChange={setSignIn} keyStored={storedSecretKept}
                            name="api-auth" />
            </Field>
          )}
          </>)}

          {/* The connector's technical settings: closed unless opened, and says so when they differ. */}
          <AdvancedSection lead={t('bots.connectors.advanced.connectorLead')}
                           inUse={connectorAdvancedInUse(cacheTtl, maxResultChars)} testId="api-advanced">
            <Field label={t('bots.connectors.api.cacheLabel')} hint={t('bots.connectors.api.cacheHint')} htmlFor="api-cache-ttl">
              <input id="api-cache-ttl" type="number" min={0} className="form-input" dir="ltr" value={cacheTtl}
                     onChange={e => setCacheTtl(Number(e.target.value) || 0)} data-testid="api-cache-ttl" />
            </Field>
            <Field label={t('bots.connectors.advanced.maxResultLabel')}
                   hint={t('bots.connectors.advanced.maxResultHint', { count: DEFAULT_MAX_RESULT_CHARS })}
                   htmlFor="api-max-result">
              <input id="api-max-result" type="number" min={500} max={40000} className="form-input" dir="ltr"
                     value={maxResultChars ?? ''} placeholder={String(DEFAULT_MAX_RESULT_CHARS)}
                     onChange={e => setMaxResultChars(e.target.value ? Number(e.target.value) : undefined)}
                     data-testid="api-max-result" />
            </Field>
          </AdvancedSection>
        </DialogColumn>

        {/* ── What the agent gets ─────────────────────────────────────── */}
        <DialogColumn title={t('bots.connectors.api.agentTitle')}
                      badge={read ? t('bots.connectors.api.operationsOn', { on: enabledCount, total: read.operations.length }) : undefined}>
          {!read ? (
            <p className="cxd__empty">{t('bots.connectors.api.noOperationsYet')}</p>
          ) : (
            <Field label={t('bots.connectors.api.operationsLabel')} hint={t('bots.connectors.api.operationsHint')}>
              {opsInUse > 0 && (
                <p className="cxa__summary" data-testid="api-advanced-actions-in-use">
                  <InUseMark /> {t('bots.connectors.advanced.actionsInUse', { count: opsInUse })}
                </p>
              )}
              <ul className="cxo" data-testid="api-operations">
                {read.operations.map(op => {
                  const s = settings[op.operation_id] || {};
                  const open = expanded === op.operation_id;
                  const inUse = operationAdvancedInUse(s);
                  return (
                    <li key={op.operation_id} className={`cxo__item ${s.enabled ? 'is-on' : ''}`}>
                      <div className="cxo__row">
                        <Switch size="small" checked={!!s.enabled} onChange={v => setOp(op.operation_id, { enabled: v })}
                                data-testid={`api-op-${op.operation_id}`} />
                        <span className="cxo__text">
                          <code className="cxo__id" dir="ltr">{op.operation_id}</code>
                          <span className="cxo__route" dir="ltr">{op.method} {op.path}</span>
                          {op.summary && <span className="cxo__summary" dir="auto">{op.summary}</span>}
                        </span>
                        <button type="button" className="cxo__toggle cxa__toggle cxa__toggle--inline"
                                onClick={() => setExpanded(open ? null : op.operation_id)} aria-expanded={open}
                                data-testid={`api-op-advanced-${op.operation_id}`}>
                          <ChevronGlyph />
                          <span>{t('bots.connectors.advanced.title')}</span>
                          {inUse && <InUseMark testId={`api-op-advanced-in-use-${op.operation_id}`} />}
                        </button>
                      </div>
                      {open && (
                        <div className="cxo__settings">
                          <p className="cxa__lead">{t('bots.connectors.advanced.actionLead')}</p>
                          <Field label={t('bots.connectors.api.fieldsLabel')} hint={t('bots.connectors.api.fieldsHint')}>
                            <textarea className="form-input form-textarea form-input--mono" rows={3} dir="ltr"
                                      value={listToLines(s.fields)}
                                      onChange={e => setOp(op.operation_id, { fields: linesToList(e.target.value) })} />
                          </Field>
                          <Field label={t('bots.connectors.api.verifiesLabel')} hint={t('bots.connectors.api.verifiesHint')}>
                            <input className="form-input form-input--mono" dir="ltr" value={(s.verifies || []).join(', ')}
                                   onChange={e => setOp(op.operation_id, { verifies: linesToList(e.target.value) })} />
                          </Field>
                          <Field label={t('bots.connectors.api.budgetLabel')} hint={t('bots.connectors.api.budgetHint')}>
                            <input type="number" min={1} max={50} className="form-input" dir="ltr" value={s.max_calls_per_turn ?? ''}
                                   onChange={e => setOp(op.operation_id, { max_calls_per_turn: e.target.value ? Number(e.target.value) : undefined })} />
                          </Field>
                          <Field label={t('bots.connectors.api.pinnedLabel')} hint={t('bots.connectors.api.pinnedHint')}>
                            <input className="form-input form-input--mono" dir="ltr" defaultValue={objToText(s.pinned)}
                                   placeholder='{"format": "json"}'
                                   onBlur={e => { const v = textToObj(e.target.value); if (v) setOp(op.operation_id, { pinned: v }); }} />
                          </Field>
                          <Field label={t('bots.connectors.api.defaultsLabel')} hint={t('bots.connectors.api.defaultsHint')}>
                            <input className="form-input form-input--mono" dir="ltr" defaultValue={objToText(s.defaults)}
                                   placeholder='{"language": "en"}'
                                   onBlur={e => { const v = textToObj(e.target.value); if (v) setOp(op.operation_id, { defaults: v }); }} />
                          </Field>
                          <Field label={t('bots.connectors.api.opDescriptionLabel')} hint={t('bots.connectors.api.opDescriptionHint')}>
                            <input className="form-input" dir="auto" value={s.description ?? ''}
                                   onChange={e => setOp(op.operation_id, { description: e.target.value })} />
                          </Field>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Field>
          )}

          <Field label={t('bots.connectors.api.instructionsLabel')} hint={t('bots.connectors.api.instructionsHint')}
                 htmlFor="api-instructions">
            <textarea id="api-instructions" className="form-input form-textarea" rows={9} value={instructions}
                      onChange={e => setInstructions(e.target.value)} maxLength={MAX_INSTRUCTIONS_CHARS} dir="auto"
                      data-testid="api-instructions" />
            <p className="cxd__count" dir="ltr">{instructions.length} / {MAX_INSTRUCTIONS_CHARS}</p>
            <GuideDraft kind="api" name={name} description={description} current={instructions}
                        onUse={setInstructions} scope={draftScope} testId="api-guide-draft"
                        emptyHint={read ? undefined : t('bots.connectors.guideDraft.readFirst')}
                        tools={(read?.operations ?? []).filter(op => settings[op.operation_id]?.enabled).map(op => ({
                          name: op.operation_id,
                          description: settings[op.operation_id]?.description || op.summary || op.description,
                          parameters: op.parameters.map(p => ({ name: p.name, type: p.type, required: p.required,
                                                                description: p.description })),
                        }))} />
          </Field>
        </DialogColumn>
      </DialogColumns>
    </>),
  });
}

// ---------------------------------------------------------------------------
// Try it
// ---------------------------------------------------------------------------

function TryDialog({ chatbotId, connector, onClose, phrase }: {
  chatbotId: string; connector: ApiConnector; onClose: () => void;
  phrase: (err: unknown, fallbackKey: string) => string;
}) {
  const { t } = useLanguage();
  const enabledOps = useMemo(() => connector.operations.filter(op => op.settings.enabled), [connector]);
  const [opId, setOpId] = useState(enabledOps[0]?.operation_id ?? '');
  const [args, setArgs] = useState<Record<string, string>>({});
  const [result, setResult] = useState<TryResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const op = enabledOps.find(o => o.operation_id === opId);
  const params = op?.parameters.filter(p => !(op.settings.pinned && p.name in op.settings.pinned)) ?? [];

  const run = async () => {
    if (!op) return;
    setError(null);
    setRunning(true);
    try {
      const typed: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(args)) {
        if (!v.trim()) continue;
        const p = op.parameters.find(x => x.name === k);
        typed[k] = p?.type === 'array' ? v.split(',').map(s => s.trim()).filter(Boolean)
          : p?.type === 'integer' || p?.type === 'number' ? Number(v)
          : p?.type === 'boolean' ? v === 'true' : v;
      }
      setResult(await apiConnectorService.tryOperation(chatbotId, connector.connector_id, op.operation_id, typed));
    } catch (err) {
      setError(phrase(err, 'bots.connectors.api.errors.generic'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <ConnectorDialog
      size="medium"
      icon={<ApiGlyph />}
      title={t('bots.connectors.api.tryTitle', { name: connector.name })}
      subtitle={t('bots.connectors.api.tryHint')}
      onClose={onClose}
      note={error}
      noteTone="error"
      testId="api-try-dialog"
      actions={(<>
        <button type="button" className="btn-cancel" onClick={onClose}>{t('common.close')}</button>
        <PrimaryAction label={t('bots.connectors.api.run')} busy={running} disabled={!op} onClick={run} testId="api-try-run" />
      </>)}
    >
      <DialogColumns>
        <DialogColumn title={t('bots.connectors.api.parametersTitle')}>
          <Field label={t('bots.connectors.api.operation')} htmlFor="api-try-op">
            <select id="api-try-op" className="form-input form-input--mono" value={opId} dir="ltr"
                    onChange={e => { setOpId(e.target.value); setArgs({}); setResult(null); }}>
              {enabledOps.map(o => (
                <option key={o.operation_id} value={o.operation_id}>{o.operation_id} — {o.method} {o.path}</option>
              ))}
            </select>
          </Field>
          {params.length === 0 && op && <p className="cxd__empty">{t('bots.connectors.api.noParameters')}</p>}
          {params.map(p => (
            <Field key={p.name} htmlFor={`api-try-arg-${p.name}`}
                   label={<><code dir="ltr">{p.name}</code>{p.required ? ' *' : ''}</>}
                   hint={p.description ? <span dir="auto">{p.description}</span> : undefined}>
              <input id={`api-try-arg-${p.name}`} className="form-input" dir="auto" value={args[p.name] ?? ''}
                     onChange={e => setArgs(a => ({ ...a, [p.name]: e.target.value }))}
                     placeholder={p.type === 'array' ? 'a, b' : ''} data-testid={`api-try-arg-${p.name}`} />
            </Field>
          ))}
        </DialogColumn>

        <DialogColumn title={t('bots.connectors.api.resultTitle')}>
          {!result ? (
            <p className="cxd__empty">{t('bots.connectors.api.noResultYet')}</p>
          ) : (
            <div className="cxr" data-testid="api-try-result">
              <div className="cxr__badges">
                <span className={`cx-pill ${result.ok ? 'cx-pill--ok' : 'cx-pill--error'}`} dir="ltr">
                  {result.status ? `HTTP ${result.status}` : t('bots.connectors.api.noResponse')}
                </span>
                {result.kind && <span className="cx-pill cx-pill--error" dir="ltr">{result.kind}</span>}
                {result.retried && <span className="cx-pill">{t('bots.connectors.api.retried')}</span>}
                <span>{t('bots.connectors.api.sizes', { raw: result.raw_chars, shaped: result.shaped_chars })}</span>
              </div>
              {result.verified.length > 0 && (
                <div className="cxr__badges">
                  <span>{t('bots.connectors.api.verifiedValues')}:</span>
                  {result.verified.map(v => <span key={v} className="cx-pill cx-pill--accent" dir="auto">{v}</span>)}
                </div>
              )}
              <pre className="cxr__body" dir="ltr">{result.result}</pre>
              <p className="cxr__request" dir="ltr">{result.request.method} {result.request.url}</p>
            </div>
          )}
        </DialogColumn>
      </DialogColumns>
    </ConnectorDialog>
  );
}

function ApiGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16" /><path d="M4 12h10" /><path d="M4 17h7" />
      <circle cx="18" cy="15" r="3" />
    </svg>
  );
}
